import {
  Badge,
  Body1,
  Body1Strong,
  Caption1,
  Divider,
  Subtitle2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import { TChangeV2 } from '../../models';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalXL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 720px)': { gridTemplateColumns: '1fr' },
  },
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  list: { margin: 0, paddingLeft: tokens.spacingHorizontalL, display: 'flex', flexDirection: 'column', gap: '2px' },
  muted: { color: tokens.colorNeutralForeground3 },
  conclusion: { display: 'flex', flexDirection: 'column', gap: '2px' },
});

// The concise assessment summary shown as a navigator item on Screen 5. The full
// decision-ready package lives on Screen 6.
export const AssessmentSummaryPanel = ({ change }: { change: TChangeV2 }) => {
  const classes = useStyles();
  const summary = change.assessment?.summary;

  if (!summary) {
    return (
      <div className={classes.root}>
        <Subtitle2>Assessment summary</Subtitle2>
        <Body1 className={classes.muted}>
          A decision-ready summary is prepared from the assessment answers and becomes the approval package on
          submission.
        </Body1>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <Subtitle2>Assessment summary</Subtitle2>
        <Badge appearance="tint" color="brand">
          Impact: {summary.impactLevel}
        </Badge>
      </div>

      <div className={classes.block}>
        <Body1Strong>Change justification</Body1Strong>
        <Body1>{summary.justification}</Body1>
      </div>

      <div className={classes.columns}>
        <div className={classes.block}>
          <Body1Strong>What is changing</Body1Strong>
          <ul className={classes.list}>
            {summary.whatChanges.map((item) => (
              <li key={item}>
                <Body1>{item}</Body1>
              </li>
            ))}
          </ul>
        </div>
        <div className={classes.block}>
          <Body1Strong>What remains unchanged</Body1Strong>
          <ul className={classes.list}>
            {summary.whatUnchanged.map((item) => (
              <li key={item}>
                <Body1>{item}</Body1>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Divider />

      <div className={classes.block}>
        <Body1Strong>Key conclusions</Body1Strong>
        {summary.keyConclusions.map((conclusion) => (
          <div key={conclusion.title} className={classes.conclusion}>
            <Body1>{conclusion.title}</Body1>
            <Caption1 className={classes.muted}>{conclusion.conclusion}</Caption1>
          </div>
        ))}
      </div>

      <div className={classes.columns}>
        <div className={classes.block}>
          <Body1Strong>Safety and performance</Body1Strong>
          <Caption1 className={classes.muted}>{summary.safetySummary}</Caption1>
        </div>
        <div className={classes.block}>
          <Body1Strong>Regulatory</Body1Strong>
          <Caption1 className={classes.muted}>{summary.regulatorySummary}</Caption1>
        </div>
      </div>
    </div>
  );
};
